// Libraries
import { format } from 'date-fns';
import { Flex, Text, Box, useMultiStyleConfig } from '@chakra-ui/react';
// Types
import { DatePickerColumnProps } from './DatePickerColumn.types';

/**
 * DatePickerColumn component represents a column in a date picker.
 *
 * @component
 * @param {DatePickerColumnProps} props - The component props.
 * @param {Date[]} props.dates - The array of dates in the column.
 * @param {string} props.formatToken - The format token for displaying the dates.
 * @param {string} props.title - The title of the column.
 * @param {Function} props.viewingDate - The function to handle viewing a date.
 * @param {Function} props.range - The function to determine if a date is within a range.
 * @param {string} props.border - The border position for the column ('left', 'right', or 'none').
 * @returns {ReactElement} DatePickerColumn component.
 */
export const DatePickerColumn = ({ dates, formatToken, title, viewingDate, range, border }: DatePickerColumnProps) => {
  const styles = useMultiStyleConfig('DatePicker');
  const borderLeft = border === 'left' ? 'void' : 'none';
  const borderRight = border === 'right' ? 'void' : 'none';
  return (
    <Box textAlign='center' borderLeft={borderLeft} borderRight={borderRight} borderColor='grey.10'>
      <Text fontSize='xs' color='waterloo.800' textTransform='uppercase' fontWeight={500}>
        {title}
      </Text>
      <Flex
        maxH={195}
        overflow='auto'
        flexDir='column'
        alignItems='center'
        sx={{
          '&::-webkit-scrollbar': {
            width: 1,
            height: 2.5,
          },
          '&::-webkit-scrollbar-thumb': {
            bgColor: 'grey.20',
            borderRadius: 'full',
          },
        }}
      >
        {dates.map((date, idx) => (
          <Box
            tabIndex={0}
            data-in-range={range(date)}
            sx={styles.month}
            key={`${date.toISOString()}-${idx}`}
            onClick={() => viewingDate(date)}
            onKeyUp={({ key }) => {
              key === 'Enter' && viewingDate(date);
            }}
          >
            <Text sx={styles.dayLabel}>{format(date, formatToken)}</Text>
          </Box>
        ))}
      </Flex>
    </Box>
  );
};
